import {
  toIsoDate, toShortDate, destructureDate, daysInMonth,
} from '../../../util/shared/tmc-global';
import { amountToNumber, roundToNearestCent, zipFormat } from '../../../util/shared/vue-global';
import { getTulsaSitusSuffix, isTulsa } from '../../../util/shared/county-specific.config';

// comment code
export const taxCommentTypesObj = {
  mtgCode: 1,
  statusCode: 2,
  ownerInfoCode: 3,
  taxRecordCode: 4,
  taxPaymentCode: 5,
  paidByChangeCode: 6,
  userCommentCode: 7,
};

/**
 * A category-7 (user) comment belongs to whoever wrote it: only that user - or a TM admin
 * (Super Admin), who overrides - may edit or delete it. Everyone else can read it but not
 * change it. Tax Admin gets no override here; it keeps its rights over the other categories.
 *
 * Converted (legacy) comments, and the ones the email/SMS flows file under category 7, carry a
 * blank userName. Nobody matches those, so they stay read-only for every regular user.
 *
 * Mirrors the server-side gate in tmc-local-backend
 * src/graphql/taxProgram/taxComments/userComment.auth.js - that one is what actually enforces
 * it, this is only what the dialog shows.
 */
export const canModifyUserComment = (comment, authUser, isTmAdmin) => {
  if (isTmAdmin) return true;
  const author = `${((comment || {}).userName) || ''}`.trim().toLowerCase();
  const me = `${authUser || ''}`.trim().toLowerCase();
  return !!author && !!me && author === me;
};

export const folderNameForUploadToBackend = {
  newTaxRoll: 'new-tax-roll',
  assessorUnzippedFolder: 'assessor-unziped-files',
};

// status code to prevent any update for consolidated-view
export const ownerPreventChangesStatusCodeList = [402, 403];

// exempt manufacturing status code
export const exemptManufacturingStatusCode = 406;

// payment done not posted (pending payment) status code
export const unpostedPaymentStatusCode = 416;

// payment done not posted, from the tax website (pending web payment) status code
export const pendingWebPaymentStatusCode = 419;

// alertStatusCodes with one of these codes has an actual pending payment behind it - the
// payment number/date should be looked up and surfaced, rather than shown as a generic
// 'has a status code' warning
export const PENDING_PAYMENT_STATUS_CODES = [
  unpostedPaymentStatusCode,
  pendingWebPaymentStatusCode,
];

export const resaleExcessUnit = 'excess';

const getTotalMonths = (fromDate, toDate) => {
  const from = new Date(fromDate);
  const to = new Date(toDate);

  const yearDiff = to.getFullYear() - from.getFullYear();
  const monthDiff = to.getMonth() - from.getMonth();

  let totalMonths = yearDiff * 12 + monthDiff;

  // Adjust if the 'toDate' day is less than the 'fromDate' day
  if (to.getDate() < from.getDate()) {
    // eslint-disable-next-line no-plusplus
    totalMonths--;
  }

  return totalMonths;
};

// Snaps a Penalty Start Date onto the county's penalty change day (e.g. change day 16 ->
// always lands on the 16th of some month), rolling to next month if the typed day is past
// the change day. Clamped to the target month's actual last day for short months (matches
// the clamping already used by stepPenaltyPeriodDate in tmc-global.js). landedDay is the
// day actually used (post-clamp) - callers announcing the adjustment to the user should
// display landedDay, not the raw penaltyChangeDay, since the two differ once clamped.
// No-ops when either input is missing so a county without a configured change day is left
// as typed (value returned exactly as passed in, not reformatted).
export const snapToPenaltyChangeDay = (dateValue, penaltyChangeDay) => {
  const penDateNumber = Number(penaltyChangeDay);
  if (!dateValue || !penDateNumber) return { value: dateValue, snapped: false, landedDay: null };
  const { year, month, day } = destructureDate(dateValue);
  const monthIndex = (month - 1) + (day <= penDateNumber ? 0 : 1);
  const clampedDay = Math.min(penDateNumber, daysInMonth(year, monthIndex));
  const value = toShortDate(new Date(Date.UTC(year, monthIndex, clampedDay)));
  const original = toShortDate(new Date(Date.UTC(year, month - 1, day)));
  return { value, snapped: value !== original, landedDay: clampedDay };
};

export const parseLocalDate = (dateString) => {
  // convert to iso date, only needed in frontend
  const convertedDate = toIsoDate(dateString);
  const date = new Date(`${convertedDate}T00:00:00`);
  return date;
};

export const isDateWithinPostingPeriod = (value, taxSettingItem) => {
  if (!value) return true;
  const { curPostPerFromDate, curPostPerToDate } = taxSettingItem || {};
  if (!curPostPerFromDate || !curPostPerToDate) return true;
  const from = parseLocalDate(curPostPerFromDate);
  const to = parseLocalDate(curPostPerToDate);
  const check = parseLocalDate(value);
  const isDateValid = from && to && check
    && !Number.isNaN(from.getTime())
    && !Number.isNaN(to.getTime())
    && !Number.isNaN(check.getTime());
  return isDateValid && check >= from && check <= to;
};

// Create SA and Create Omit expose no adjustment-date field at all, so the server stamps
// adjDate with its own today and refuses the save when today is outside the open posting
// period (see util/postingPeriodGuard.js in the backend). Surface that up front rather than
// letting the clerk fill in a whole form first. Returns '' when the entry is allowed - the
// server stays the authority, this is only the early warning.
// Uses LOCAL date parts on purpose: the server stamps with moment(), which is also local, so a
// UTC-based "today" would disagree with it for part of every evening.
//
// allowBeforePeriod: Zach, 2026-09-29. Create Omit / Create SA no longer refuse a today that
// falls BEFORE the open period - the server files the record under the period's first day and
// tells the user. So those two buttons must NOT block on that case any more, or the clerk never
// reaches the form and the shift can never happen. A today AFTER the period end still blocks:
// the period has not been rolled that far yet, which is a real problem.
export const entryDateOutsidePostingPeriodMsg = (
  taxSettingItem, today = new Date(), { allowBeforePeriod = false } = {},
) => {
  const { curPostPerFromDate, curPostPerToDate } = taxSettingItem || {};
  if (!curPostPerFromDate || !curPostPerToDate) return '';
  const month = `${today.getMonth() + 1}`.padStart(2, '0');
  const day = `${today.getDate()}`.padStart(2, '0');
  const entryDate = `${month}-${day}-${today.getFullYear()}`;
  if (isDateWithinPostingPeriod(entryDate, taxSettingItem)) return '';
  if (allowBeforePeriod) {
    const from = parseLocalDate(curPostPerFromDate);
    const check = parseLocalDate(entryDate);
    if (from && check && !Number.isNaN(from.getTime()) && !Number.isNaN(check.getTime())
      && check < from) return '';
  }
  // Reformat from the raw config STRING, not via parseLocalDate - that returns a local
  // midnight, and toShortDate reads UTC parts off a Date, so round-tripping a Date through
  // the two shifts the day by one in any timezone ahead of UTC. Handed a string, toShortDate
  // normalises MM-DD-YYYY and ISO alike without ever building a local-midnight Date.
  return `The entry date ${entryDate} does not match the current posting period `
    + `${toShortDate(curPostPerFromDate)} - ${toShortDate(curPostPerToDate)}. `
    + 'Roll the posting period forward before entering this activity.';
};

// Only a COMPLETE date is judged - MM-DD-YYYY / MM/DD/YYYY as typed in the date control, an ISO
// date or instant, or a Date - so a half-typed "07-0" does not flash a warning on every keystroke.
const COMPLETE_DATE = /^(\d{1,2}[-/]\d{1,2}[-/]\d{4}|\d{4}-\d{2}-\d{2}.*)$/;

// A payment's Business Date may not fall BEFORE the open posting period. Tulsa 2023-191970: payment
// #101877 was keyed 07/01/2026 while October was open; the fiscal-year check passed, so it saved,
// could not be posted, and went unseen for 18 days. Only the before side is refused - counties key
// next month's payments before they roll the period. Returns '' when the date is allowed.
// The server (postingPeriodGuard.js assertPayDateNotBeforePostingPeriod) is the authority and uses
// the same wording; this is the early warning.
export const payDateBeforePostingPeriodMsg = (payDate, taxSettingItem) => {
  const { curPostPerFromDate, curPostPerToDate } = taxSettingItem || {};
  if (!payDate || !curPostPerFromDate || !curPostPerToDate) return '';
  if (!(payDate instanceof Date) && !COMPLETE_DATE.test(String(payDate).trim())) return '';
  const day = toIsoDate(typeof payDate === 'string' ? payDate.trim() : payDate);
  if (!day || day.includes('NaN') || day >= toIsoDate(curPostPerFromDate)) return '';
  return `Business Date ${toShortDate(payDate, '/')} is before the current posting period `
    + `(${toShortDate(curPostPerFromDate, '/')} - ${toShortDate(curPostPerToDate, '/')}). `
    + 'Payments can\'t be entered into a closed period.';
};

export const penaltyCalculation = (
  penaltyDate, penaltyStartDate, penaltyChangeDay, totalTax, totalPaid,
  feeTotal, penaltyRate, totalDue, taxYear, statusCode = [], taxSettingItem = {},
) => {
  const { halfPayAmountThreshold } = taxSettingItem;
  // if status code is 406, then return 0 for penalty and also for fee
  const isExemptManufacturing = (statusCode || []).includes(exemptManufacturingStatusCode);
  if (isExemptManufacturing) {
    return { penaltyAmt: 0, penaltyWithOrWithoutFees: 0, getPenRateWithMonths: 0 };
  }

  // formatting penaltyDate
  // if we convert to short date, then it helps with day light savings time issues
  const penaltyDateFormatted = penaltyDate ? parseLocalDate(penaltyDate) : new Date();
  const checkTaxYearIsValidOrNot = Number.isNaN(Number(taxYear)) ? undefined : Number(taxYear);
  const formattedTotalTax = Number.isNaN(Number(totalTax)) ? 0 : Number(totalTax);
  const formattedTotalPaid = Number.isNaN(Number(totalPaid)) ? 0 : Number(totalPaid);
  const formattedPenRate = Number.isNaN(Number(penaltyRate)) ? 0 : Number(penaltyRate);
  const formattedFeeTotal = Number.isNaN(Number(feeTotal)) ? 0 : Number(feeTotal);
  const formattedtotalDue = Number.isNaN(Number(totalDue)) ? 0 : Number(totalDue);
  const stillDue = formattedTotalTax - formattedTotalPaid;
  const currentYear = checkTaxYearIsValidOrNot + 1;

  // to get half payment (conditions)
  // If the partial payment is < 45% then we would treat this tax record as unpaid,
  //    (meaning that the penalty would start in January)
  // If the partial payment is > or = 45% then we would treat this as a half payment(meaning
  //    that the penalty would start in April.)
  // const getHalfPayment = (formattedTotalPaid * 2) >= formattedTotalTax;
  const formattedHalfPayAmountThreshold = Number.isNaN(Number(halfPayAmountThreshold))
    ? 0.45 : Number(halfPayAmountThreshold);
  const getHalfPayment = (formattedTotalPaid) >= (formattedTotalTax
     * formattedHalfPayAmountThreshold);
  // making date format(yy-mm-dd) for penaltyChangeDay
  const checkPenaltyChangeDay = penaltyChangeDay || undefined;
  const penaltyChangeDayFormat = checkPenaltyChangeDay ? `${currentYear}-${1}-${checkPenaltyChangeDay}` : undefined;
  // for half payment it will be 1st april, to get the format
  const halfPaymentDayFormat = getHalfPayment && checkPenaltyChangeDay ? `${currentYear}-${4}-${checkPenaltyChangeDay}` : undefined;

  // penaltyStartDate will override penaltyChangeDay is penaltyStartDate is present
  const penDate = penaltyStartDate || halfPaymentDayFormat || penaltyChangeDayFormat;
  const penDateFormatted = parseLocalDate(penDate);
  penaltyDateFormatted.setHours(0, 0, 0, 0);
  penDateFormatted.setHours(0, 0, 0, 0);

  // check overpayment
  const overPayment = formattedTotalTax > formattedTotalPaid;
  let penaltyWithOrWithoutFees = formattedFeeTotal; // initially will contains fees
  // condition to check penaltyDate(year) greater than or equal to current tax year

  if ((penaltyDateFormatted.getUTCFullYear() >= checkTaxYearIsValidOrNot)
    && formattedtotalDue && overPayment) {
    // condition to check penaltyStartDate or penaltyChangeDay is less than penaltyDate
    if (penDateFormatted <= penaltyDateFormatted) {
      // to get difference in dates
      // const differencInDates = new Date(
      //   penaltyDateFormatted.getTime() - penDateFormatted.getTime(),
      // );

      // get the difference in years and months
      // const yearsDiffence = differencInDates.getUTCFullYear() - 1970;
      // const monthsDiffence = differencInDates.getUTCMonth();
      // this will give us total months
      // let totalMonthsDifference = (yearsDiffence * 12) + monthsDiffence;
      // if (totalMonthsDifference >= 0) {
      //   totalMonthsDifference += 1;
      // }

      let totalMonthsDifference = getTotalMonths(penDateFormatted, penaltyDateFormatted);
      if (totalMonthsDifference >= 0) {
        totalMonthsDifference += 1;
      }
      let getPenRateWithMonths = (formattedPenRate) * totalMonthsDifference;
      // calculation ( stillDue * penalty-rate * penalty-term ) / 100
      let penaltyAmt = parseFloat(roundToNearestCent((stillDue * getPenRateWithMonths) / 100));
      // Cap the penalty at 100% of the original tax amount
      const maxPenalty = stillDue;
      if (penaltyAmt > maxPenalty) {
        penaltyAmt = maxPenalty;
      }
      if (getPenRateWithMonths > 100) {
        getPenRateWithMonths = 100;
      }
      // calculation (( stillDue * penalty-rate * penalty-term ) / 100) + fees
      penaltyWithOrWithoutFees = penaltyAmt + formattedFeeTotal;

      return { penaltyAmt, penaltyWithOrWithoutFees, getPenRateWithMonths };
    }
  }
  return { penaltyAmt: 0, penaltyWithOrWithoutFees, getPenRateWithMonths: 0 };
};

export const getTodaysDate = () => new Date(`${new Date().toISOString().split('T')[0]}T00:00:00.000Z`);

export const getTaxYearStatus = (date = new Date()) => {
  const resultStatus = {
    currentTax: '',
    backTax: '',
    priorTax: '',
    nextTax: '',
  };
  const givenDate = (typeof date.getMonth === 'function') ? date : new Date();

  const month = givenDate.getMonth() + 1;
  const year = givenDate.getFullYear();
  // July 1 is the cut-off for a current Tax Year
  let currentTaxYearStart = '';
  if (month <= 6) {
    currentTaxYearStart = new Date(`7/1/${givenDate.getFullYear() - 1}`);
  } else {
    currentTaxYearStart = new Date(`7/1/${year}`);
  }

  resultStatus.nextTax = currentTaxYearStart.getFullYear() + 1;
  resultStatus.currentTax = currentTaxYearStart.getFullYear();
  resultStatus.priorTax = currentTaxYearStart.getFullYear() - 1;
  resultStatus.backTax = currentTaxYearStart.getFullYear() - 2;

  return resultStatus;
};

export const taxIsDelinquent = (taxRecord, penaltyDay, currentDate = getTodaysDate()) => {
  const taxYearStatus = getTaxYearStatus();
  const isPriorOrBackTaxYear = taxYearStatus.currentTax > taxRecord.taxYear;
  let penaltyStartDate = new Date(taxRecord.penaltyStartDate);

  //    prior or back tax year
  if (isPriorOrBackTaxYear) {
    if (taxRecord.totalDue <= 0) {
      return false;
    }
    const isNoPaymentDone = taxRecord.totalDue !== 0;
    const isFirstHalfPaymentDone = taxRecord.totalDue !== 0 && taxRecord.totalPaid
      < taxRecord.totalTax;
    if (isNoPaymentDone) {
      penaltyStartDate = new Date(`1/${penaltyDay}/${taxRecord.taxYear}`);
    } else if (isFirstHalfPaymentDone) {
      penaltyStartDate = new Date(`4/${penaltyDay}/${taxRecord.taxYear}`);
    }
    return penaltyStartDate < currentDate;
  }
  // current tax year
  const isFullPaymentDone = taxRecord.totalDue <= 0;
  if (isFullPaymentDone) {
    return false;
  }
  const isNoPaymentDone = taxRecord.totalDue !== 0;
  const isFirstHalfPaymentDone = taxRecord.totalDue !== 0 && taxRecord.totalPaid
    < taxRecord.totalTax;
  if (isNoPaymentDone) {
    penaltyStartDate = new Date(`1/${penaltyDay}/${taxRecord.taxYear}`);
  } else if (isFirstHalfPaymentDone) {
    penaltyStartDate = new Date(`4/${penaltyDay}/${taxRecord.taxYear}`);
  }
  return penaltyStartDate < currentDate;
};

export const useAbortController = () => new AbortController();

export const commonAdjFuncForTaxrollNOwner = (returnedVal, adjType) => {
  const adjItem = {
    taxYear: returnedVal.taxYear,
    taxId: returnedVal.taxId,
    taxInquiryTransaction: returnedVal._id,
    schoolDistrict: returnedVal.schoolDistrict,
    type: adjType || '',
    certDate: toShortDate(),
  };
  adjItem.valuesAfterAdjustment = {
    assessorPenalty: returnedVal.assessorPenalty,
    exemptions: returnedVal.exemptions,
    improvements: returnedVal.improvements,
    land: returnedVal.land,
    mobileHome: returnedVal.mobileHome,
    totalTax: returnedVal.totalTax,
  };
  adjItem.valuesBeforeAdjustment = {
    assessorPenalty: returnedVal.assessorPenalty,
    exemptions: returnedVal.exemptions,
    improvements: returnedVal.improvements,
    land: returnedVal.land,
    mobileHome: returnedVal.mobileHome,
    totalTax: returnedVal.totalTax,
    name: `${returnedVal.name1 || ''} ${returnedVal.name2 || ''}`,
    schoolDistrict: returnedVal.schoolDistrict,
  };
  return adjItem;
};

export const commonFuncForConsolidateView = (editedItem, withoutNewLine = false) => {
  const arrOfKeysOne = ['name1', 'name2'];
  const arrOfKeysTwo = ['inCareOf1', 'inCareOf2'];
  const arrOfKeysThree = ['address1', 'address2', 'address3', 'address4'];
  const arrOfKeysFour = ['city', 'state', 'zip'];

  // Step 1: Names
  const val1 = arrOfKeysOne
    .map((ft) => (editedItem[ft] && editedItem[ft].trim()) || '')
    .filter(Boolean);

  // Step 2: In-care-of, first one gets C/O. % and C/O mean the same thing, so
  // if address1 already leads with %, don't also print C/O.
  const addressStartsWithPercent = (editedItem.address1 || '').trim().startsWith('%');
  const val2 = addressStartsWithPercent ? [] : arrOfKeysTwo
    .map((ft) => (editedItem[ft] && editedItem[ft].trim()) || '')
    .filter(Boolean)
    .map((line, index) => (index === 0 ? `C/O ${line}` : line));

  // Step 3: Addresses
  const val3 = arrOfKeysThree
    .map((ft) => (editedItem[ft] && editedItem[ft].trim()) || '')
    .filter(Boolean);

  // Step 4: City + State + ZIP merged
  let val4 = arrOfKeysFour
    .map((ft, i) => (i === 2 ? zipFormat(editedItem[ft]) : (editedItem[ft] && editedItem[ft].trim()) || ''))
    .filter(Boolean);
  val4 = [val4.join(' ')]; // Merge into one line

  const val5 = editedItem.country ? [editedItem.country] : [];

  // Combine all
  const allLines = [...val1, ...val2, ...val3, ...val4, ...val5];
  if (!withoutNewLine) return allLines.join('\n');
  return allLines.join(' ');
};

// Tulsa prints the unit/lot (situsSuffix) only on mobile home records, as '#<suffix>'; every other
// county prints it as is. Pass countyName so the screens match the address the backend saves.
export const getSitusAddress = (upsert, countyName = '') => {
  const situsAddressArr = [];
  const situsSuffix = isTulsa(countyName)
    ? getTulsaSitusSuffix(upsert.taxType, upsert.situsSuffix)
    : upsert.situsSuffix;
  if (upsert.situsStreetNumber) situsAddressArr.push(upsert.situsStreetNumber);
  if (upsert.situsStreetDirection) situsAddressArr.push(upsert.situsStreetDirection);
  if (upsert.situsStreetName) situsAddressArr.push(upsert.situsStreetName);
  if (upsert.situsStreetType) situsAddressArr.push(upsert.situsStreetType);
  if (situsSuffix) situsAddressArr.push(situsSuffix);
  if (upsert.situsTown) situsAddressArr.push(upsert.situsTown);
  return (situsAddressArr || []).join(' ').trim() || '';
};

export const buildMailingAddress = (item) => {
  if (!item) return '';
  // % and C/O mean the same thing, so if address1 already leads with %, don't also show inCareOf.
  const addressStartsWithPercent = (item.address1 || '').trim().startsWith('%');
  const parts = [
    item.name1,
    item.name2,
    addressStartsWithPercent ? '' : item.inCareOf1,
    addressStartsWithPercent ? '' : item.inCareOf2,
    item.address1,
    item.address2,
    item.address3,
    item.address4,
    [item.city, item.state, item.zip].filter(Boolean).join(', '),
    item.country,
  ];
  return parts.filter(Boolean).join('\n');
};

// The per-field owner changes a save reported, trimmed to exactly the three keys
// ownerChangeInputType accepts. Apollo stamps __typename onto every object it returns, and
// handing that straight back to upsertTaxComments would be rejected as an unknown field.
export const toOwnerChangesInput = (ownerChanges) => (ownerChanges || [])
  .map(({ field, from, to }) => ({ field, from, to }));

// Resolves the printable C/O name for a tax record: inCareOf1 (with any '%' marker
// stripped) plus inCareOf2 when present. Returns '' when the record has no C/O name,
// so callers can fall back to the owner name (name1).
export const getPrintableCareOfName = (transaction = {}) => {
  const careOf1 = (transaction.inCareOf1 || '').replace(/%/g, '').trim();
  const careOf2 = (transaction.inCareOf2 || '').replace(/%/g, '').trim();
  if (!careOf1) return '';
  return careOf2 ? `${careOf1} ${careOf2}` : careOf1;
};

// alertStatusCodes are formatted strings like '416 - Pending Payment', not bare numbers -
// parse the leading code before comparing against PENDING_PAYMENT_STATUS_CODES.
export const partitionPendingPaymentAlertCodes = (alertStatusCodes) => {
  const pendingCodes = [];
  const otherCodes = [];
  (alertStatusCodes || []).forEach((codeStr) => {
    if (PENDING_PAYMENT_STATUS_CODES.includes(parseInt(codeStr, 10))) {
      pendingCodes.push(codeStr);
    } else {
      otherCodes.push(codeStr);
    }
  });
  return { pendingCodes, otherCodes };
};

export const getHighlightColorForStatusCode = [
  { text: '', value: '' },
  { text: 'Green', value: '#81C784' },
  { text: 'Indigo', value: '#9FA8DA' },
  { text: 'Purple', value: '#B39DDB' },
  { text: 'Teal', value: '#80CBC4' },
  { text: 'Orange', value: '#FFB74D' },
  { text: 'Yellow', value: '#ffe5006b' },
];

// { varName, varCol } pairs for CRUDList's variableToChangeRowBgColor prop: one
// entry per selectable highlight color, checked against a flag of the same name.
export const getStatusCodeRowColorMap = () => getHighlightColorForStatusCode.map((item) => (
  { varName: item.value, varCol: item.value }
));

// Flags each status code row with its own highlightColor as a boolean key, the
// shape variableToChangeRowBgColor expects to pick a row's background color.
export const applyStatusCodeHighlight = (items) => (items || []).map((item) => (
  item.highlightColor ? { ...item, [item.highlightColor]: true } : item
));

/**
 * A refund/credit row built by the Protested Tax screen's Create Tax Refunds / Release
 * (ProtestedTaxFooter.postprocessData).
 *
 * These are the only tax details that must keep `isFromProtest` on the way to the server:
 * posting uses it to draw the protest record's Amt Under Protest down and mark it Resolved.
 * Every other screen sets the same flag purely to word a warning, so it is stripped there.
 */
export const isProtestRefundOrCredit = (taxDetail) => !!(taxDetail && taxDetail.isFromProtest)
  && (taxDetail.taxType === 'TAXREFUND' || taxDetail.taxType === 'TAXCREDIT');

/** The payment type a tax detail row is created as unless the clerk picked another one. */
export const DEFAULT_TAX_PAYMENT_TYPE = 'TAXPAYMENT';

/**
 * The payment TYPE a tax detail carries (taxType), as every Type dropdown offers it.
 *
 * Clerks key these by number - 1 Tax Payment, 2 Tax Refund, ... 7 Other - and the number is
 * nothing more than the enum's position, so it is derived here once rather than in each of the
 * places that render the list: the payment dialog's Add Tax Records band, the large payment
 * grid's Type cell, TaxEditDialog and dialogTaxDetails.
 *
 * `short` is what a closed dropdown in a grid row shows (the number alone, which is all the
 * column has room for); `label` is the full "2 Tax Refund" the menu and the band show.
 *
 * @param {Array} statusTypes  state.enums.TaxPaymentEnum - [{ name, description }], in order
 * @returns {Array} [{ value: 'TAXREFUND', short: '2', label: '2 Tax Refund' }, ...]
 */
export const buildTaxPaymentTypeOptions = (statusTypes) => (statusTypes || [])
  .map((el, index) => ({
    value: el.name,
    short: String(index + 1),
    label: `${index + 1} ${el.description}`,
  }));

/**
 * Oklahoma no longer lets the counter hand pennies back as change, so a cash total that does
 * not land on a nickel is nudged onto one by a fee (see pennyRoundingFeeAmount). 555 is the
 * fee number every county is seeded with; Tax Settings can point elsewhere.
 */
export const PENNY_ROUNDING_DEFAULT_FEE_NUMBER = 555;

/**
 * The Penny Rounding fee that puts `totalDue` on the nearest $0.05: always one of
 * -0.02, -0.01, +0.01, +0.02, or 0 when the total is already on a nickel.
 *
 * Cents granularity means there is never a .025 tie to break, so "nearest" is unambiguous:
 * a remainder of 1 or 2 cents rounds down, 3 or 4 rounds up.
 */
export const pennyRoundingFeeAmount = (totalDue) => {
  const cents = Math.round(amountToNumber(totalDue) * 100);
  // A negative total (refund row) still has to land on a nickel, and JS % keeps the sign -
  // re-normalise so the rounding table below reads the same either side of zero.
  const remainder = ((cents % 5) + 5) % 5;
  if (remainder === 0) return 0;
  return (remainder <= 2 ? -remainder : 5 - remainder) / 100;
};

/** True when `totalDue` is not a multiple of $0.05 and so needs a Penny Rounding fee. */
export const needsPennyRounding = (totalDue) => pennyRoundingFeeAmount(totalDue) !== 0;

/* ---------------------------------------------------------------------------------------------
   Assessor X-Ref levy comparison (Special Tax Items > New Tax Roll > Assessor X-Ref)

   The tab exists to show which assessor codes the treasurer's levies do not agree with. The two
   levy columns are recomputed by the backend after every tax roll / tax levy import
   (refreshAccessorXRefLevies); these turn that into something the page says out loud, rather than
   leaving the clerk to compare two columns of mills by eye across hundreds of rows.
--------------------------------------------------------------------------------------------- */

/** Row background colors, picked to read as "look at this" without claiming to be an error. */
export const X_REF_LEVY_MISMATCH_COLOR = '#ffcdd2'; // red - the two sides disagree
export const X_REF_LEVY_CONFLICT_COLOR = '#ffe0b2'; // amber - the FILE disagrees with itself

export const getXRefLevyRowColorMap = () => [
  { varName: 'levyMismatch', varCol: X_REF_LEVY_MISMATCH_COLOR },
  // Listed last so it wins on a row that is both: a file that cannot agree with itself about a
  // code has to be sorted out before the assr-vs-treasurer comparison means anything.
  { varName: 'levyConflict', varCol: X_REF_LEVY_CONFLICT_COLOR },
];

/**
 * Why one X-Ref row's two levy columns disagree, in the words the clerk needs.
 *
 * `null` assessorLevies means this year's file carried no record for the code at all, which is a
 * different thing from the file saying zero - the backend leaves the column blank for it, and
 * saying so here is what stops it reading as "the assessor levies nothing".
 */
export const describeXRefLevyCheck = (row) => {
  const {
    assessorLevies, treasuresLevies, assessorLeviesValues, assessorLeviesConflict, taxDistrictCode,
  } = row || {};

  if (assessorLeviesConflict) {
    const distinct = (assessorLeviesValues || []).join(' / ');
    return `File disagrees with itself: ${distinct}`;
  }
  if (assessorLevies === null || assessorLevies === undefined) {
    return 'No records in this year\'s file';
  }
  if (!taxDistrictCode) return 'No tax district on this x-ref row';
  if (amountToNumber(assessorLevies) !== amountToNumber(treasuresLevies)) {
    return 'Assr does not match Treasurer';
  }
  return '';
};

/**
 * Adds the Levy Check wording and the two row-highlight flags CRUDList's
 * variableToChangeRowBgColor looks for. Used as the X-Ref grid's postProcessTrans.
 */
export const applyXRefLevyCheck = (rows) => (rows || []).map((row) => {
  const levyCheck = describeXRefLevyCheck(row);
  return {
    ...row,
    levyCheck,
    levyConflict: !!row.assessorLeviesConflict,
    // Only a real disagreement between the two columns is flagged red. A code with no records in
    // the file is not a mismatch - there is nothing to compare it against yet.
    levyMismatch: !!levyCheck && !row.assessorLeviesConflict
      && row.assessorLevies !== null && row.assessorLevies !== undefined,
  };
});
